import type {ChangelogResp} from "data/changelog/api";
import {
  CommitIcon,
  CubeIcon,
  LayersIcon,
  GearIcon,
  BarChartIcon,
  Crosshair2Icon,
  BellIcon,
  MobileIcon,
  Component1Icon,
  FileTextIcon,
  TokensIcon,
  MixerHorizontalIcon,
} from "@radix-ui/react-icons";
import {ChangelogOverview} from "./changelog-overview";
import {MiniChangelogSkeleton} from "./skeleton-mini-changelog";
import {useQuery} from "@tanstack/react-query";
import {Changelog} from "data/changelog";
import {useRootStore} from "stores";
import type UIStore from "stores/UIStore";
import {observer} from "mobx-react";
import dayjs from "dayjs";

type Props = {
  changelogs?: ChangelogResp[];
};

export const Icons = {
  analytix: {
    main: <CubeIcon className="text-secondary-foreground" />,
    mini: <CubeIcon color="white" />,
  },
  "on-site": {
    main: <MobileIcon className="text-secondary-foreground" />,
    mini: <MobileIcon color="white" />,
  },
  alarms: {
    main: <BellIcon className="text-secondary-foreground" />,
    mini: <BellIcon color="white" />,
  },
  diagnostix: {
    main: <Crosshair2Icon className="text-secondary-foreground" />,
    mini: <Crosshair2Icon color="white" />,
  },
  analysis: {
    main: <BarChartIcon className="text-secondary-foreground" />,
    mini: <BarChartIcon color="white" />,
  },
  chi: {
    main: <Component1Icon className="text-secondary-foreground" />,
    mini: <Component1Icon color="white" />,
  },
  "kappa-x": {
    main: <LayersIcon className="text-secondary-foreground" />,
    mini: <LayersIcon color="white" />,
  },
  "tau-x": {
    main: <TokensIcon className="text-secondary-foreground" />,
    mini: <TokensIcon color="white" />,
  },
  reports: {
    main: <FileTextIcon className="text-secondary-foreground" />,
    mini: <FileTextIcon color="white" />,
  },
  settings: {
    main: <GearIcon className="text-secondary-foreground" />,
    mini: <GearIcon color="white" />,
  },
  api: {
    main: <MixerHorizontalIcon className="text-secondary-foreground" />,
    mini: <MixerHorizontalIcon color="white" />,
  },
  generic: {
    main: <CommitIcon className="text-secondary-foreground" />,
    mini: <CommitIcon color="white" />,
  },
} as const;

// NOTE: in order of priority
export const Tags = [
  "alarms",
  "diagnostix",
  "analysis",
  "analytix",
  "on-site",
  "chi",
  "kappa-x",
  "tau-x",
  "reports",
  "api",
  "settings",
] as const;

function tagKey(tags: string[]) {
  for (const key of Tags) {
    if (tags.includes(key)) return key;
  }
  return "generic";
}

function formatDate(date: Date) {
  const d = dayjs(date);
  return `${d.format("D")} ${d.format("MMMM")}`;
}

function MiniChangelogComponent({changelogs}: Props) {
  const {uiStore} = useRootStore() as unknown as {uiStore: UIStore};
  const images = useQuery(Changelog.queries.images(changelogs));

  return (
    <div
      className="flex flex-col border border-content-main-menu-hover rounded-md"
      style={{zIndex: 60, fontFamily: "Montserrat, sans-serif"}}
    >
      {changelogs?.map((item, idx) => {
        const key = tagKey(item.tags);
        const icon = Icons[key].mini;
        const releasedAt = formatDate(new Date(item.released_at));

        return (
          <ChangelogOverview
            releasedAt={releasedAt}
            changelog={item}
            isImagesFetched={images.isSuccess}
            imageCache={images.data}
            key={item.changelog_id}
          >
            <div
              style={{height: 29}}
              className={`flex flex-row pl-4 items-center justify-between pr-2 hover:bg-content-main-menu-hover py-2 cursor-pointer transition-colors duration-100 text-gray-100 hover:text-white font-thin ${idx === 0 && "rounded-t-sm"} ${idx === changelogs.length - 1 && "rounded-b-sm pt-4"}`}
            >
              <div className="flex flex-row items-center truncate">
                <div className="mr-4">{icon}</div>
                <div className="flex flex-col truncate">
                  <div
                    className="truncate"
                    style={{fontSize: "1.20rem"}}
                  >
                    {item.title}
                  </div>
                  <div
                    className="text-gray-300"
                    style={{fontSize: "1.10rem"}}
                  >
                    {releasedAt}
                  </div>
                </div>
              </div>
              <div className="mx-2">
                {!item.seen && !uiStore.seenChangelogs[item.changelog_id] && (
                  <div
                    style={{width: 7, height: 7}}
                    className="rounded-full bg-content-main-menu-notification"
                  ></div>
                )}
              </div>
            </div>
          </ChangelogOverview>
        );
      })}
      {!changelogs && <MiniChangelogSkeleton />}
    </div>
  );
}
export const MiniChangelog = observer(MiniChangelogComponent);
